export const onEscape = (
  fn: (event: KeyboardEvent) => void,
  signal?: AbortSignal,
) => {
  document.addEventListener(
    "keydown",
    (event) => {
      if (event.code === "Escape") {
        fn(event);
      }
    },
    {
      signal,
    },
  );
};

export const onPointerDown = (
  fn: (event: PointerEvent) => void,
  signal?: AbortSignal,
) => {
  document.addEventListener("pointerdown", fn, {
    signal,
  });
};

export const isFormElement = (
  target: Element | EventTarget | null,
): target is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement => {
  if (target instanceof Element) {
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement
    ) {
      return true;
    }
  }

  return false;
};

type IsolatedEventHandler = <
  T extends Element,
  E extends UIEvent,
  K extends React.SyntheticEvent<T, E>,
>(
  handler?: (event: K) => void,
) => (event: K) => void;

/**
 * Creates an isolated event handler that prevents the default action and stops
 * event propagation.
 *
 * @param handler - The event handler to be executed.
 * @returns A new event handler that prevents the default action and stops
 * event propagation.
 */
export const createIsolatedEventHandler: IsolatedEventHandler = (handler) => {
  return (event) => {
    event.preventDefault();
    event.stopPropagation();
    handler?.(event);
  };
};

/**
 * Creates an event handler that stops event propagation.
 *
 * @param handler - The event handler to be executed.
 * @returns A new event handler that stops event propagation.
 */
export const stopPropagationEventHandler: IsolatedEventHandler = (handler) => {
  return (event) => {
    event.stopPropagation();
    handler?.(event);
  };
};

/**
 * Creates an event handler that prevents the default action.
 *
 * @param handler - The event handler to be executed.
 * @returns A new event handler that prevents the default action.
 */
export const preventDefaultEventHandler: IsolatedEventHandler = (handler) => {
  return (event) => {
    event.preventDefault();
    handler?.(event);
  };
};

/**
 * The **`startViewTransition()`** method of the Document interface starts a new same-document (SPA) view transition and returns a ViewTransition object to represent it.
 *
 * [MDN Reference](https://developer.mozilla.org/docs/Web/API/Document/startViewTransition)
 */
export const startViewTransition = (
  updateCallback?: ViewTransitionUpdateCallback,
  options?: Omit<StartViewTransitionOptions, "update">,
): ViewTransition => {
  try {
    return document.startViewTransition({
      ...options,
      update: updateCallback,
    });
  } catch (error) {
    updateCallback?.();

    throw error;
  }
};

/**
 * Detects the top layer for a DOM element.
 *
 * Returns the closest dialog ancestor (top layer) or falls back to document.body.
 *
 * @param domReference - The DOM element to check
 * @returns The closest dialog element, or document.body if none found
 */
export const getClosestLayer = (domReference: Element | null) => {
  const dialog = domReference?.closest("dialog");

  return dialog ?? document.body;
};

export const setHTMLContentAndExecute = (container: Element, html: string) => {
  container.innerHTML = "";

  const range = document.createRange();

  range.selectNode(container);

  const fragment = range.createContextualFragment(html);

  container.appendChild(fragment);
};

export class SetToggle<T> extends Set<T> {
  toggle(value: T, force?: boolean) {
    const shouldAdd = force ?? this.has(value) === false;

    if (shouldAdd) {
      this.add(value);
    } else {
      this.delete(value);
    }

    return shouldAdd;
  }
}
